import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Coins, Menu, X, LayoutGrid } from "lucide-react";
import { get } from "@/lib/api";

const NAV = [
  { to: "/", label: "Ana Sayfa", end: true },
  { to: "/kesfet", label: "Keşfet" },
  { to: "/rezervasyonlarim", label: "Rezervasyonlarım" },
  { to: "/kredi", label: "Kredi" },
  { to: "/profil", label: "Profil" },
];

function CreditBadge() {
  const { data } = useQuery({ queryKey: ["me"], queryFn: () => get("/me") });
  return (
    <Link
      to="/kredi"
      data-testid="credit-balance-badge"
      className="flex items-center gap-2 rounded-full bg-[#2D2926] px-4 py-2 text-sm font-medium text-[#FDFBF7] transition-colors hover:bg-[#8C6A5D]"
    >
      <Coins className="h-4 w-4 text-[#E8DED1]" />
      <span data-testid="credit-balance-value">{data?.credits ?? "—"}</span>
      <span className="text-[#E8DED1]">kredi</span>
    </Link>
  );
}

export default function ConsumerLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#FDFBF7] font-body text-[#2D2926]">
      <header className="sticky top-0 z-50 border-b border-white/40 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#8C6A5D] text-[#FDFBF7]">
              <span className="font-display text-xl font-semibold leading-none">X</span>
            </div>
            <span className="font-display text-2xl font-semibold tracking-tight">XYZ</span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm transition-colors ${
                    isActive ? "bg-[#E8DED1] text-[#2D2926]" : "text-[#5C5856] hover:text-[#2D2926]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block"><CreditBadge /></div>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden"
              onClick={() => setOpen(!open)}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t border-[#E5E0D8] bg-white/90 px-5 py-3 lg:hidden">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2.5 text-sm ${isActive ? "bg-[#E8DED1]" : "text-[#5C5856]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-2"><CreditBadge /></div>
          </div>
        )}
      </header>

      <main key={location.pathname}>
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-[#E5E0D8] bg-[#F4EFE7]">
        <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
          <div className="grid gap-10 md:grid-cols-4">
            <div className="md:col-span-2">
              <span className="font-display text-3xl font-semibold">XYZ</span>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#5C5856]">
                Fitness, wellness ve güzellik dünyasını tek bir üyelikle keşfedin.
                Stüdyoları gezin, seans bulun ve kredinizle anında rezervasyon yapın.
              </p>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#8C6A5D]">Keşfet</h4>
              <ul className="space-y-2 text-sm text-[#5C5856]">
                <li><Link to="/kesfet" className="hover:text-[#2D2926]">Stüdyolar</Link></li>
                <li><Link to="/rezervasyonlarim" className="hover:text-[#2D2926]">Rezervasyonlar</Link></li>
                <li><Link to="/kredi" className="hover:text-[#2D2926]">Kredi Paketleri</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#8C6A5D]">Paneller</h4>
              <ul className="space-y-2 text-sm text-[#5C5856]">
                <li><Link to="/" data-testid="footer-consumer" className="flex items-center gap-2 hover:text-[#2D2926]"><LayoutGrid className="h-4 w-4" /> Kullanıcı</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-[#E5E0D8] pt-6 text-xs text-[#8C8480]">
            © 2026 XYZ · Demo MVP prototipi · İstanbul
          </div>
        </div>
      </footer>
    </div>
  );
}
